import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { CheckCircleIcon, FlashIcon, XIcon } from "@/app/components/system/Icons";

export type ToastVariant = "success" | "error";

export interface Toast {
  id: string;
  variant: ToastVariant;
  message: string;
  leaving?: boolean;
}

interface ToastContextValue {
  success: (message: string) => string;
  error: (message: string) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const MAX_STACK = 3;
const DURATION = { success: 3500, error: 5000 } as const;
const LEAVE_MS = 180;

let counter = 0;
const nextId = () => `t${Date.now().toString(36)}${(counter++).toString(36)}`;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const clearTimer = useCallback((id: string) => {
    const t = timers.current.get(id);
    if (t) {
      clearTimeout(t);
      timers.current.delete(id);
    }
  }, []);

  const remove = useCallback((id: string) => {
    setToasts((curr) => curr.filter((t) => t.id !== id));
    clearTimer(id);
  }, [clearTimer]);

  const dismiss = useCallback((id: string) => {
    clearTimer(id);
    setToasts((curr) => curr.map((t) => t.id === id ? { ...t, leaving: true } : t));
    const exitTimer = setTimeout(() => remove(id), LEAVE_MS);
    timers.current.set(id, exitTimer);
  }, [clearTimer, remove]);

  const push = useCallback((variant: ToastVariant, message: string) => {
    const id = nextId();
    setToasts((curr) => {
      const next = [...curr, { id, variant, message }];
      return next.length > MAX_STACK ? next.slice(next.length - MAX_STACK) : next;
    });
    const stayTimer = setTimeout(() => dismiss(id), DURATION[variant]);
    timers.current.set(id, stayTimer);
    return id;
  }, [dismiss]);

  useEffect(() => {
    const map = timers.current;
    return () => { map.forEach((t) => clearTimeout(t)); map.clear(); };
  }, []);

  const value = useMemo<ToastContextValue>(() => ({
    success: (message) => push("success", message),
    error: (message) => push("error", message),
    dismiss,
  }), [push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}

const VIEWPORT =
  "fixed left-1/2 -translate-x-1/2 top-[18px] z-50 w-[min(calc(100%-32px),360px)] flex flex-col items-center gap-2 pointer-events-none";

const TOAST_BASE =
  "pointer-events-auto inline-flex items-center gap-2 bg-paper border border-line rounded-full pl-3.5 pr-1.5 py-2 font-sans text-[13px] text-ink max-w-full shadow-card motion-reduce:animate-none";

const TOAST_VARIANT: Record<ToastVariant, string> = {
  success: "",
  error: "bg-[oklch(0.97_0.025_50)]",
};

const ICON_VARIANT: Record<ToastVariant, string> = {
  success: "text-moss",
  error: "text-terra",
};

const DISMISS =
  "w-7 h-7 rounded-full grid place-items-center text-ink-3 bg-transparent border-0 cursor-pointer transition-colors duration-150 hover:text-ink hover:bg-cream-2";

function ToastViewport({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: string) => void }) {
  if (toasts.length === 0) return null;
  return (
    <div className={VIEWPORT} role="region" aria-live="polite" aria-label="Notifications">
      {toasts.map((t) => {
        const animation = t.leaving
          ? "animate-[toast-out_180ms_ease-in_forwards]"
          : "animate-[toast-in_180ms_ease-out]";
        return (
          <div
            key={t.id}
            className={`${TOAST_BASE} ${TOAST_VARIANT[t.variant]} ${animation}`}
            role={t.variant === "error" ? "alert" : "status"}
          >
            <span className={`inline-flex items-center justify-center shrink-0 ${ICON_VARIANT[t.variant]}`} aria-hidden="true">
              {t.variant === "success" ? <CheckCircleIcon size={16} /> : <FlashIcon size={16} />}
            </span>
            <span className="flex-1 min-w-0 leading-snug truncate">{t.message}</span>
            <button
              type="button"
              className={DISMISS}
              onClick={() => onDismiss(t.id)}
              aria-label="Dismiss"
            >
              <XIcon size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
